/**
 * Setup's smaller parts: the report type the case is drafted as (one the
 * playbook offers this client), and the price — set, suggested, or proposed
 * by the case agent — with the turnaround or a due date, as today's pricing
 * takes them.
 */

import { useState } from 'react';

import { Button, Group, NumberInput, Select, Stack, Switch, Text, TextInput } from '@mantine/core';

import { vnextApiClient } from '@/services/vnext-api';
import type { WorkbenchSetup } from '@/types/workbench';
import { formatMoney } from '@/utils';
import { useSetupActions } from './setupActions';
import { SetupProposals } from './SetupProposals';

export function SetupReportType({ setup }: Readonly<{ setup: WorkbenchSetup }>) {
  const actions = useSetupActions();
  const { reportType, proposals } = setup;
  const chosen = reportType.code
    ? (reportType.offered.find((o) => o.code === reportType.code)?.name ?? reportType.name)
    : null;
  return (
    <Stack gap="xs" data-testid="setup-report-type">
      <SetupProposals proposals={proposals} kinds={['report_type']} />
      {actions.editable ? (
        <Select
          size="xs"
          label="Drafted as"
          description="One of the report types the playbook offers this client"
          placeholder="Not chosen yet"
          data={reportType.offered.map((o) => ({
            value: o.code,
            label: o.name ? `${o.code} · ${o.name}` : o.code,
          }))}
          value={reportType.code}
          onChange={(value) =>
            void actions.run(
              'report-type',
              () => vnextApiClient.setSetupReportType(actions.reportId, value || null),
              value ? `Drafted as ${value}.` : 'Report type cleared.'
            )
          }
          clearable
          w={320}
          disabled={actions.pending === 'report-type'}
          data-testid="setup-report-type-select"
        />
      ) : (
        <Text size="sm">
          {reportType.code ? `${reportType.code}${chosen ? ` · ${chosen}` : ''}` : 'Not chosen'}
        </Text>
      )}
      {reportType.offered.length === 0 ? (
        <Text size="xs" c="dimmed">
          The playbook offers no report type for this case&apos;s config.
        </Text>
      ) : null}
    </Stack>
  );
}

export function SetupPrice({ setup }: Readonly<{ setup: WorkbenchSetup }>) {
  const actions = useSetupActions();
  const { price, suggested, turnaroundDays, unsuggested } = setup.price;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<string | number>(price?.amount ?? suggested?.amount ?? '');
  const [currency, setCurrency] = useState<string | null>(
    price?.currency ?? suggested?.currency ?? 'GBP'
  );
  const [days, setDays] = useState<string | number>(turnaroundDays ?? '');
  const [dueAt, setDueAt] = useState('');
  const [expedited, setExpedited] = useState(false);
  const set = price ? formatMoney(price.amount, price.currency) : null;
  const suggestion = suggested ? formatMoney(suggested.amount, suggested.currency) : null;

  const save = async () => {
    const saved = await actions.run(
      'price',
      () =>
        vnextApiClient.setSetupPrice(actions.reportId, {
          price: amount === '' ? null : String(amount),
          currency,
          ...(dueAt
            ? { dueAt: new Date(dueAt).toISOString() }
            : { turnaroundDays: days === '' ? null : Number(days) }),
          expedited,
        }),
      'Price set.'
    );
    if (saved) {
      setOpen(false);
    }
  };

  return (
    <Stack gap="xs" data-testid="setup-price">
      <SetupProposals proposals={setup.proposals} kinds={['price']} />
      <Group gap="md" wrap="wrap">
        <Text size="sm" data-testid="setup-price-set">
          {set ? `Set: ${set}` : 'Not set yet'}
          {turnaroundDays != null ? ` · ${turnaroundDays} days` : ''}
        </Text>
        {suggestion ? (
          <Text size="sm" c="dimmed">
            Suggested: {suggestion} — the report config&apos;s price
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            No suggestion{unsuggested ? `: ${unsuggested}` : '.'}
          </Text>
        )}
        {(actions.editable || actions.commercialsEditable) && !open ? (
          <Button
            size="compact-xs"
            variant="default"
            onClick={() => setOpen(true)}
            data-testid="setup-price-edit"
          >
            {set ? 'Change' : 'Set the price'}
          </Button>
        ) : null}
      </Group>
      {open ? (
        <Group gap="xs" align="flex-end" wrap="wrap" data-testid="setup-price-form">
          <NumberInput
            size="xs"
            label="Price"
            min={0}
            decimalScale={2}
            value={amount}
            onChange={setAmount}
            w={130}
            data-testid="setup-price-amount"
          />
          <Select
            size="xs"
            label="Currency"
            data={['GBP', 'EUR', 'USD']}
            value={currency}
            onChange={setCurrency}
            w={100}
            allowDeselect={false}
          />
          <NumberInput
            size="xs"
            label="Turnaround (days)"
            min={0}
            max={365}
            value={days}
            onChange={setDays}
            disabled={dueAt !== ''}
            w={140}
            data-testid="setup-price-days"
          />
          <TextInput
            size="xs"
            type="date"
            label="Or a fixed due date"
            value={dueAt}
            onChange={(e) => setDueAt(e.currentTarget.value)}
            w={160}
          />
          <Switch
            size="xs"
            label="Expedited"
            checked={expedited}
            onChange={(e) => setExpedited(e.currentTarget.checked)}
            pb={6}
          />
          <Button
            size="xs"
            disabled={amount === ''}
            loading={actions.pending === 'price'}
            onClick={() => void save()}
            data-testid="setup-price-save"
          >
            Save
          </Button>
          <Button size="xs" variant="subtle" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
}
